import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { CustomerConstants, IconDefinitionConstants } from "utils/constants";
import { CustomerFormTypes } from "models/formTypes";
import { DialogEnums, StyleEnums } from "models/enums";
import { CustomerReferencePersonsSectionProps } from "models/propTypes/customerPropTypes";
//#endregion
//#region components
import { FormFieldLayout, GroupSection } from "components/common/wintForms";
import { Chip, Grid } from "@mui/material";
import { Icon, MenuButton, RoundButton, StyledButton, TableAsync } from "components/common";
import ReferencePersonFormContainer from "../../referencePersonFormContainer";
import { useCustomerService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const CustomerReferencePersonsSection: React.FC<CustomerReferencePersonsSectionProps> = ({
  connect,
  customerId,
  isCreate,
}) => {
  const customerService = useCustomerService();

  const entityText = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Person).toLowerCase();

  const dispatch = useDispatch();

  const referencePersonFieldProps = connect("generalInfo.referencePersons");

  const onAddNewClick = () => {
    dispatch(
      displayDialog({
        isOpen: false,
        title: `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NewEntity)} ${entityText}`,
        titleIcon: IconDefinitionConstants.WBusinessCard,
        dialogType: DialogEnums.DialogType.Content,
        actions: [
          {
            text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Add),
          },
        ],
        children: (
          <ReferencePersonFormContainer
            customerId={customerId}
            handleSubmit={async (refPerson: CustomerFormTypes.ReferencePerson) => {
              const referencePersons = [...(referencePersonFieldProps.value ?? [])];

              if (refPerson.isDefault) {
                referencePersons.forEach((person: CustomerFormTypes.ReferencePerson) => {
                  if (person.id !== refPerson.id) {
                    person.isDefault = false;
                  }
                });
              }
              referencePersons.push({ ...refPerson, id: refPerson.id ? refPerson.id : UtilsHelper.getUniqueId() });
              referencePersonFieldProps.setValue(referencePersons);
            }}
          />
        ),
        maxWidth: "sm",
      })
    );
  };

  const onItemClick = (referencePerson: CustomerFormTypes.ReferencePerson) => {
    dispatch(
      displayDialog({
        isOpen: false,
        title: `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Edit)} ${
          referencePerson.name ?? entityText
        }`,
        titleIcon: IconDefinitionConstants.WBusinessCard,
        dialogType: DialogEnums.DialogType.Content,
        actions: [
          {
            text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Save),
          },
        ],
        children: (
          <ReferencePersonFormContainer
            {...referencePerson}
            customerId={customerId}
            handleSubmit={async (refPerson: CustomerFormTypes.ReferencePerson) => {
              const referencePersons = [...(referencePersonFieldProps.value ?? [])];

              const personIndex = referencePersons.findIndex(
                (v: CustomerFormTypes.ReferencePerson) => v.id === refPerson.id
              );

              if (personIndex > -1) {
                if (refPerson.isDefault) {
                  referencePersons.forEach((person: CustomerFormTypes.ReferencePerson) => {
                    if (person.id !== refPerson.id) {
                      person.isDefault = false;
                    }
                  });
                }

                referencePersons[personIndex] = refPerson;
                referencePersonFieldProps.setValue(referencePersons);
              }
            }}
          />
        ),
        maxWidth: "sm",
      })
    );
  };

  const removeItemFromList = async (referencePerson: CustomerFormTypes.ReferencePerson) => {
    const id = referencePerson.id;
    const referencePersons = [...(referencePersonFieldProps.value ?? [])];

    const personIndex = referencePersons.findIndex((v: CustomerFormTypes.ReferencePerson) => v.id === id);

    if (personIndex > -1) {
      // on edit, directly remove from backend
      if (!isCreate && id && !UtilsHelper.isClientGeneratedId(id)) {
        await customerService.deleteReferencePerson(id);
      }

      referencePersons.splice(personIndex, 1);
      referencePersonFieldProps.setValue(referencePersons);
    }
  };

  return (
    <GroupSection
      header={TranslationKeys.Invoice.ReferencePersons}
      icon={IconDefinitionConstants.WUser}
      navKey={CustomerConstants.SettingsNavKeys.Reference}
    >
      <FormFieldLayout
        direction="horizontal"
        label={TranslationKeys.Invoice.Persons}
        descriptionText={TranslationKeys.Invoice.ReferencePersonsHelpText}
        childGridSize={8}
      >
        <Grid container direction="column" rowSpacing={2} className={cssExports.tableListContainer}>
          <Grid item>
            <StyledButton size="sm" onClick={onAddNewClick}>
              {`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.AddEntity)} ${entityText}`}
            </StyledButton>
          </Grid>
          <TableAsync<CustomerFormTypes.ReferencePerson>
            properties={{
              name: {
                formatter: (value: CustomerFormTypes.ReferencePerson) => (
                  <Grid item container direction="column">
                    <Grid item className={cssExports.tableListMainText}>
                      {value.name}
                    </Grid>
                    {value.referenceId && <Grid item>{`ID: ${value.referenceId}`}</Grid>}
                  </Grid>
                ),
                noWrap: true,
              },
              email: {
                formatter: (value: CustomerFormTypes.ReferencePerson) => (
                  <Grid item container direction="column">
                    {value.email && <Grid item>{value.email}</Grid>}
                    {value.phoneNumber && <Grid item>{value.phoneNumber}</Grid>}
                  </Grid>
                ),
              },
              isDefault: {
                formatter: (row: CustomerFormTypes.ReferencePerson) =>
                  row.isDefault ? (
                    <Chip
                      label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Default)}
                      className={cssExports.greenTag}
                    />
                  ) : (
                    ""
                  ),
                alignment: "right",
              },
              select: {
                formatter: (row: any) => (
                  <MenuButton
                    menuPlacement="bottom"
                    popperOffSet={[16, 2]}
                    menuItems={[
                      {
                        description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Delete),
                        icon: IconDefinitionConstants.WTrashAlt,
                        highlightType: StyleEnums.DisplayType.Danger,
                        onClickHandler: removeItemFromList.bind(null, row),
                      },
                    ]}
                    button={
                      <RoundButton className={cssExports.tableListMoreActions}>
                        <Icon icon={IconDefinitionConstants.WEllipsisH} />
                      </RoundButton>
                    }
                    classes={{
                      menuOpen: cssExports.tableListMoreActionsMenuOpen,
                    }}
                  />
                ),
                width: 24,
                className: cssExports.moreActions,
              },
            }}
            rowClickHandler={onItemClick}
            filter={referencePersonFieldProps.value ?? []}
            pagination
            disableQueryPagination
            maxRowCount={40}
            hideHeader
            orderByProperty={"name"}
            hidePaginationIfSinglePage
          />
        </Grid>
      </FormFieldLayout>
    </GroupSection>
  );
};

export default CustomerReferencePersonsSection;
